import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Search, Plus, Settings, Sun, Moon, MessageCircle, X, Pencil } from 'lucide-react'
import { useChatStore } from '../../store/chatStore'
import { useAuthStore } from '../../store/authStore'
import { getConversations, createConversation, searchUsers } from '../../lib/api'
import ChatListItem from './ChatListItem'
import ChatListSkeleton from './ChatListSkeleton'
import ChatView from './ChatView'
import Avatar from '../ui/Avatar'

export default function MainChatScreen() {
  const { conversations, activeConversation, setConversations, setActiveConversation } = useChatStore()
  const { user } = useAuthStore()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'unread' | 'groups'>('all')
  const [showChat, setShowChat] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('zumo-theme') as 'dark' | 'light') || 'dark'
  )

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('zumo-theme', theme)
  }, [theme])

  useEffect(() => {
    console.log('Zumo: user:', user?.username, '| uid:', user?.uid)
    if (user) loadConversations()
  }, [user])

  const loadConversations = async () => {
    setListLoading(true)
    try {
      const d = await getConversations()
      console.log('Zumo: conversations loaded:', d?.conversations?.length || 0)
      if (d.conversations) setConversations(d.conversations)
    } catch (e: any) {
      console.error('Zumo: failed to load conversations:', e.message || e)
    }
    setListLoading(false)
  }

  const handleSearch = async (q: string) => {
    setSearch(q)
    if (q.length < 2) { setSearchResults([]); return }
    try { const d = await searchUsers(q); if (d.users) setSearchResults(d.users.filter((u: any) => u.uid !== user?.uid)) } catch {}
  }

  const startChat = async (u: any) => {
    try {
      const d = await createConversation('dm', [u.uid])
      if (d.conversation) { await loadConversations(); setActiveConversation(d.conversation); setShowChat(true); setShowSearch(false); setSearch('') }
    } catch {}
  }

  const filtered = useMemo(() => {
    let f = conversations
    if (filter === 'unread') f = f.filter((c: any) => c.unread > 0)
    if (filter === 'groups') f = f.filter((c: any) => c.type === 'group')
    return f
  }, [conversations, filter])

  if (showChat && activeConversation) {
    return <ChatView onBack={() => { setShowChat(false); setActiveConversation(null); loadConversations() }} />
  }

  return (
    <div className="screen grid-bg">
      <header className="main-header glass">
        <div>
          <h1 className="logo">Zumo</h1>
          <p className="header-subtitle">{filtered.length} conversation{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} className="icon-btn">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Link to="/settings" className="icon-btn"><Settings size={18} /></Link>
        </div>
      </header>

      <div className="search-section">
        <div className="search-box" onClick={() => setShowSearch(true)}>
          <Search size={16} color="var(--text-muted)" />
          <span className="search-placeholder">Search users...</span>
        </div>
      </div>

      <div className="filters">
        {(['all', 'unread', 'groups'] as const).map(f => (
          <button key={f} className={`filter-pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'unread' ? 'Unread' : 'Groups'}
          </button>
        ))}
      </div>

      <div className="chat-list">
        <AnimatePresence mode="wait">
          {listLoading ? (
            <ChatListSkeleton count={6} />
          ) : filtered.length === 0 ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="empty-state">
              <MessageCircle size={56} strokeWidth={1} color="var(--text-muted)" />
              <h2>Welcome to Zumo</h2>
              <p>Private messaging. Groups. Voice calls.<br/>Fast, secure, real-time.</p>
              <button className="start-btn" onClick={() => setShowSearch(true)}>Start chatting</button>
            </motion.div>
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {filtered.map((c: any) => (
                <ChatListItem key={c.id} convo={c} isActive={activeConversation?.id === c.id}
                  onClick={() => { setActiveConversation(c); setShowChat(true) }} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.button whileTap={{ scale: 0.9 }} className="fab" onClick={() => setShowSearch(true)}>
        <Pencil size={22} strokeWidth={2.5} />
      </motion.button>

      <AnimatePresence>
        {showSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="modal-overlay" onClick={() => { setShowSearch(false); setSearch('') }}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="search-modal" onClick={e => e.stopPropagation()}>
              <div className="search-modal-header">
                <X size={20} onClick={() => { setShowSearch(false); setSearch('') }} style={{ cursor: 'pointer' }} />
                <span>New Chat</span>
                <div />
              </div>
              <div className="search-input-wrapper">
                <Search size={16} color="var(--text-muted)" />
                <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by username..." autoFocus />
              </div>
              <div className="search-results">
                {searchResults.map((u: any) => (
                  <div key={u.uid} className="search-user" onClick={() => startChat(u)}>
                    <Avatar name={u.username} size={44} />
                    <div className="user-info">
                      <span>{u.display_name || u.username}</span>
                      <span className="user-sub">@{u.username}</span>
                    </div>
                  </div>
                ))}
                {search.length >= 2 && searchResults.length === 0 && (
                  <div className="no-results">No users found for "{search}"</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
